/**
 * 選択素材のメディア種別まわりの純関数ヘルパー。
 *
 * 静止画生成と動画プロンプト入力モードは選択スコープ (STILL_IMAGE_SCOPE) を
 * 共有するため、静止画側に動画 (reference_video) や音声 (reference_audio) の
 * 素材が残ったまま来ることがある。静止画で「使える/使えない」の判定は
 * ペイロード構築 (GenerationPreviewPanel) とグレーアウト表示
 * (GenerationWorkspace → SelectedAssetsPanel) の両方から参照されるため、
 * ここに一本化して乖離を防ぐ。
 */
import type { AudioReferenceUsage, GenerationAsset } from './types';
import { isCheckedLayerStack, parseLayerStack } from '../../../features/motion/layerStack';

// source_type → 参照音声の既定用途 (docs/features/reference-audio-roles-plan.md §2.5
// 優先順位3)。narration/bgm はシステムが生成時に自動付与する取得元、sound_logo は
// サウンドロゴ生成物の source_type (backend/routes/brands/sound_logos.py)。
// SE集カタログ由来の推定はフェーズ5 (カタログ未実装) まで対象外。
const SOURCE_TYPE_DEFAULT_AUDIO_USAGE: Record<string, AudioReferenceUsage> = {
  narration: 'voice',
  bgm: 'bgm',
  sound_logo: 'sound_logo',
};

// 参照音声チップの用途ラベル (docs/features/reference-audio-roles-plan.md §2.1)。
// SelectedAssetsPanel (用途ドロップダウン) と AudioCatalogPickerModal (フェーズ5b の
// カタログバッジ) の両方が使うため、ここに一本化する (ラベル文言の乖離を防ぐ)。
export const AUDIO_USAGE_LABELS: Record<AudioReferenceUsage, string> = {
  voice: '声',
  bgm: 'BGM',
  sound_effect: '効果音',
  sound_logo: 'サウンドロゴ',
};

export const AUDIO_USAGE_OPTIONS: Array<{ value: AudioReferenceUsage; label: string }> = [
  { value: 'voice', label: '声' },
  { value: 'bgm', label: 'BGM' },
  { value: 'sound_effect', label: '効果音' },
  { value: 'sound_logo', label: 'サウンドロゴ' },
];

// 参照音声として添付できる音声フォーマット (計画書 §2.6)。素材ライブラリ自体は
// audio/aac 等も広く許可するが (UploadMaterialModal の accept 拡張)、動画モデルへの
// 参照音声としての添付は wav/mp3 のみ許可する (MiniMax H3 の既存制約を一般化。
// backend 側は SEEDANCE_REFERENCE_AUDIO_CONSTRAINTS / MINIMAX_REFERENCE_AUDIO_CONSTRAINTS
// の allowed_content_types で同じ制約を持つ)。
const REFERENCE_AUDIO_ALLOWED_CONTENT_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];

/** 音声素材を参照音声として添付できる形式か。非音声素材は対象外 (常に true)。 */
export function isReferenceAudioFormatAllowed(asset: Pick<GenerationAsset, 'contentType'>): boolean {
  const contentType = (asset.contentType ?? '').toLowerCase();
  if (!contentType.startsWith('audio/')) return true;
  return REFERENCE_AUDIO_ALLOWED_CONTENT_TYPES.includes(contentType);
}

/**
 * 動画モデル別の参照音声の最短クリップ尺 (秒)。バックエンドの
 * SEEDANCE_REFERENCE_AUDIO_CONSTRAINTS / SEEDANCE_25_REFERENCE_AUDIO_CONSTRAINTS /
 * MINIMAX_REFERENCE_AUDIO_CONSTRAINTS の min_clip_seconds と一致させること
 * (backend/services/scenario/clients/video_providers/{seedance,minimax}_provider.py)。
 * これ未満の音声はバックエンド検証でリクエスト全体がエラーになるため、FE 側で
 * 事前にグレーアウト・送信除外する (isReferenceAudioBelowMinClipSeconds)。
 * 参照音声非対応モデル (それ以外) は null (制約なし＝グレーアウトしない)。
 */
export function getReferenceAudioMinClipSeconds(videoModelKey: string | null | undefined): number | null {
  const key = (videoModelKey ?? '').trim().toLowerCase();
  if (!key) return null;
  if (key.startsWith('seedance') || key.startsWith('dreamina-seedance')) return 1.8;
  if (key.startsWith('minimax')) return 2;
  return null;
}

/**
 * 参照音声チップが選択中の動画モデルの最短クリップ尺未満か。
 * 尺が不明 (durationSeconds が取れない) 素材は誤ってグレーアウトしないよう false を返す
 * (実際の下限チェックはバックエンドに任せ、そのまま送信する)。
 * usage!=='reference_audio' の素材は対象外 (常に false)。
 */
export function isReferenceAudioBelowMinClipSeconds(
  asset: Pick<GenerationAsset, 'usage' | 'durationSeconds'>,
  videoModelKey: string | null | undefined,
): boolean {
  if (asset.usage !== 'reference_audio') return false;
  const durationSeconds = asset.durationSeconds;
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds)) return false;
  const minClipSeconds = getReferenceAudioMinClipSeconds(videoModelKey);
  if (minClipSeconds === null) return false;
  return durationSeconds < minClipSeconds;
}

/** 参照音声の用途の既定値を推定する (§2.5 優先順位2→3)。
 *  呼び出し側 (addAsset 等) は、これより優先度の高い「ユーザーの明示選択」
 *  (既存の asset.audioUsage) を先にチェックすること。
 *  1. materials.default_audio_role (バックエンド未実装の間は常に undefined)
 *  2. source_type によるシステム推定
 *  3. どちらも無ければ undefined (添付時にユーザーへ選択を要求する)。 */
export function inferDefaultAudioUsage(asset: GenerationAsset): AudioReferenceUsage | undefined {
  if (asset.defaultAudioRole) return asset.defaultAudioRole;
  const sourceType = asset.sourceType ?? undefined;
  return sourceType ? SOURCE_TYPE_DEFAULT_AUDIO_USAGE[sourceType] : undefined;
}

/** 動画素材のフレーム指定 (最初/最後のフレーム) で抽出済みのフレーム画像 URL。
 *  フレーム未指定 (「動画そのまま」) や未抽出のときは null。 */
export function resolveVideoFrameRefUrl(asset: GenerationAsset): string | null {
  const frame =
    asset.videoReferenceMode === 'first_frame' ? asset.videoFrameMaterials?.first
    : asset.videoReferenceMode === 'last_frame' ? asset.videoFrameMaterials?.last
    : undefined;
  return frame?.url || null;
}

/** 静止画の生成に参考画像として使える素材か。
 *  音声は不可。動画はフレーム指定 (抽出済みフレームあり) のときのみ可。 */
export function isStillImageUsableAsset(asset: GenerationAsset): boolean {
  const contentType = asset.contentType ?? '';
  if (contentType.startsWith('audio/') || asset.usage === 'reference_audio') return false;
  if (contentType.startsWith('video/') || asset.usage === 'reference_video') {
    return !!resolveVideoFrameRefUrl(asset);
  }
  return true;
}

/**
 * 動画生成の buildVideoPayload (useVideoHandlers.ts) が reference_images /
 * reference_material_ids へ合流させる素材かの判定。音声素材・参照動画そのもの
 * (video-to-video 用。フレーム抽出済みの場合は別経路で合流するためここでは対象外)
 * を除く。
 *
 * 注意: usage='reference' が主だが、フレームモードから参照モードへ切り替えた際の
 * 残留値 (usage='first_frame'/'last_frame'。scope 共有でシナリオ側フレームモードから
 * 持ち越されるケース) も画像系であれば buildVideoPayload はそのまま参考画像として
 * 送る (二重管理を避けるため buildVideoPayload 側もこの関数を使う。挙動は変えない)。
 * @メンション候補判定にはこの関数をそのまま使わず、より狭い
 * isMentionableReferenceAsset を使うこと (下記参照)。
 */
export function isReferenceImageCandidate(asset: GenerationAsset): boolean {
  const contentType = asset.contentType ?? '';
  if (asset.usage === 'reference_audio' || contentType.startsWith('audio/')) return false;
  const isReferenceVideo = (asset.usage ?? 'reference') === 'reference_video';
  if (isReferenceVideo || contentType.startsWith('video/')) return false;
  return true;
}

/** frame_generator が保存する生成フレーム (scene-intro 画像) の source_type
 *  (BE: materials.source_type='scenario'。静止画履歴の除外と同じ識別子)。 */
export const GENERATED_SCENE_FRAME_SOURCE_TYPE = 'scenario';
/** 生成フレームのファイル名マーカー (BE frame_generator: `scene-intro_<timestamp>.<ext>`)。
 *  sourceType が乗っていない素材 (この判定の導入前に scope へ追加された素材・古い復元経路)
 *  向けのフォールバック識別子。 */
const GENERATED_SCENE_FRAME_FILENAME_MARKER = 'scene-intro_';

/**
 * 生成フレーム (シーンの最初/最後のフレーム画像。frame_generator 由来) かどうか。
 *
 * シーン単位で制作するシナリオでは、生成したフレームが後続シーンのフレーム生成の参考用に
 * 「選択中素材」へ自動追加される (useFrameHandlers)。その素材が動画生成の
 * reference_images にまで流れると、フレーム画像が first_frame ではなく参照画像として
 * 動画モデルへ渡ってしまう (2026-09-10 Seedance の faceSafe シーンでイラストフレームが
 * 参照画像に混入した障害)。フレーム画像を動画モデルへ渡す経路は first_frame / last_frame
 * だけという契約なので、動画生成の参照画像収集 (useVideoHandlers) はこの判定で除外する。
 * バックエンド (references._drop_generated_scene_frame_urls) にも同じ除外がある。
 *
 * 判定: sourceType === 'scenario' を正とし、sourceType 未設定の素材はファイル名の
 * `scene-intro_` マーカーで補う。動画から抽出したフレーム (video_first_frame /
 * video_last_frame) はユーザーが参考画像として明示選択するものなので対象外。
 */
export function isGeneratedSceneFrameAsset(asset: GenerationAsset): boolean {
  if (asset.sourceType === GENERATED_SCENE_FRAME_SOURCE_TYPE) return true;
  if (asset.sourceType) return false;
  for (const candidate of [asset.storagePath, asset.sourceUrl, asset.thumbnailUrl]) {
    if (!candidate) continue;
    const path = candidate.split('?')[0].split('#')[0];
    const basename = path.slice(path.lastIndexOf('/') + 1);
    if (basename.includes(GENERATED_SCENE_FRAME_FILENAME_MARKER)) return true;
  }
  return false;
}

/**
 * 選択中素材 `assets` のうち `id` の素材が生成フレームかどうか。
 *
 * フレームのクリア / 再生成 / シーン削除では、生成時に自動追加したフレーム素材だけを
 * 選択中素材から片付ける。ユーザーが自分で選んだ素材 (素材パネル・選択中素材から
 * シーンへドラッグしたもの) は同じ id でもフレームから外すだけで選択中素材には残す。
 */
export function isGeneratedSceneFrameAssetId(assets: GenerationAsset[], id: string): boolean {
  const asset = assets.find((a) => a.id === id);
  return !!asset && isGeneratedSceneFrameAsset(asset);
}

/**
 * 素材 @メンション (docs/features/material-at-mention-plan.md) の候補判定。
 * 「現在の生成モードで reference_images / reference_material_ids に載り得る素材」の
 * うち、usage が未設定または 'reference' の画像系素材に限定する。
 *
 * isReferenceImageCandidate との違い: フレームモードの残留値 (usage='first_frame'/
 * 'last_frame') は buildVideoPayload 上は参考画像として送られ得るが、ユーザーが
 * 明示的に選んだ「参考画像」とは性質が異なる (フレームモード切替の名残であることが
 * 多い) ため、@メンションの候補には出さない。first_frame / last_frame /
 * reference_video / reference_audio はいずれも対象外
 * (挿入できるのに @ImageN へ解決できないトークンを作らないため)。
 */
export function isMentionableReferenceAsset(asset: GenerationAsset): boolean {
  if (!isReferenceImageCandidate(asset)) return false;
  const usage = asset.usage ?? 'reference';
  return usage === 'reference';
}

/**
 * 画像系素材の短辺が minShortSide 未満か (Seedance/BytePlus の参照画像下限対策)。
 *
 * BytePlus は参照画像の各辺 300px 未満を 400 で拒否する (アップスケールはしない方針。
 * バックエンドは fetch_reference_image_payloads で同じ閾値でスキップするフェイルセーフを
 * 持つため、ここでの判定はあくまで UI のグレーアウト・送信前除外の最適化)。
 *
 * width/height が両方わかっている画像系素材のみ判定する。動画・音声素材は対象外
 * (isReferenceImageCandidate と同条件で除外)。width/height が欠けている素材は
 * false を返す (誤ってグレーアウトしない。実際の下限チェックはバックエンドが担う)。
 */
export function isBelowMinReferenceSize(asset: GenerationAsset, minShortSide: number): boolean {
  if (!isReferenceImageCandidate(asset)) return false;
  const { width, height } = asset;
  if (typeof width !== 'number' || typeof height !== 'number') return false;
  return Math.min(width, height) < minShortSide;
}

/**
 * レイヤー素材 (モーショングラフィックスの入力) か。素材の `generation_metadata.layer_stack`
 * の有無で判定する (docs/features/motion-graphics-mode-plan.md §3.3・§3.5)。
 * 選択チップ (GenerationAsset.layerStack) と素材 API の行 (generation_metadata) の両方を受ける。
 * 形が崩れた layer_stack (parseLayerStack が null) と入稿ゲート未通過 (integrity.checked !== true) は
 * レイヤー素材として扱わない (fail-closed)。
 */
export function isLayerStackMaterial(asset: {
  layerStack?: unknown;
  generation_metadata?: Record<string, unknown> | null;
}): boolean {
  // 入稿ゲートを通った (integrity.checked === true) ものだけ (generate の 400 と揃える・§6.1)
  return isCheckedLayerStack(parseLayerStack(asset.layerStack))
    || isCheckedLayerStack(parseLayerStack(asset.generation_metadata?.layer_stack));
}
